import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiZap, FiAward, FiUsers, FiMapPin, FiTarget, FiHeart } = FiIcons;

const About = () => {
  const milestones = [
    {
      year: '2010',
      title: 'Foundation',
      description: 'Started as a small electrical service provider in Fatehgarh, Baramulla'
    },
    {
      year: '2015',
      title: 'Expansion',
      description: 'Expanded services to include CCTV and security systems installation'
    },
    {
      year: '2018',
      title: 'Smart Solutions',
      description: 'Introduced smart home automation and IoT solutions'
    },
    {
      year: '2020',
      title: 'Solar Power',
      description: 'Added renewable energy solutions with solar power systems'
    },
    {
      year: '2023',
      title: 'Digital Transformation',
      description: 'Launched AI-powered customer service and online booking system'
    }
  ];

  const values = [
    {
      icon: FiTarget,
      title: 'Our Mission',
      description: 'To provide reliable, innovative electrical and automation solutions that enhance safety, comfort, and efficiency in every home and business we serve.',
      color: 'text-blue-400'
    },
    {
      icon: FiHeart,
      title: 'Our Vision',
      description: 'To be the leading electrical service provider in Kashmir, known for excellence, innovation, and customer satisfaction.',
      color: 'text-red-400'
    },
    {
      icon: FiAward,
      title: 'Our Values',
      description: 'Quality craftsmanship, transparent pricing, timely service, and building long-term relationships with our customers.',
      color: 'text-green-400'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-blue-900/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-40 h-40 bg-blue-500/30 rounded-full blur-xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-cyan-500/30 rounded-full blur-xl animate-pulse" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-8"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300">
              <SafeIcon icon={FiZap} className="text-lg" />
              <span>About Tayyab Electric Studio</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold">
              <span className="gradient-text">Powering Kashmir</span>
              <br />
              <span className="text-white">Since 2010</span>
            </h1>

            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              A trusted unit of ARS Trading Corporation, we've been serving Fatehgarh, Baramulla, 
              and the entire Kashmir region with professional electrical and automation solutions for over 13 years.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6">
                <span className="gradient-text">Our Story</span>
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p>
                  What started as a small electrical service business in 2010 has grown into one of Kashmir's 
                  most trusted names in electrical and automation solutions. As a proud unit of ARS Trading Corporation, 
                  we combine the agility of a local business with the backing of a established trading house.
                </p>
                <p>
                  Our journey began with a simple mission: to provide reliable electrical services to our community. 
                  Over the years, we've evolved with technology, expanding our expertise to include CCTV systems, 
                  smart home automation, solar power solutions, and comprehensive security systems.
                </p>
                <p>
                  Today, with over 500 successful projects and 200+ satisfied customers, we continue to innovate 
                  and adapt to meet the changing needs of modern homes and businesses in the region.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-2xl p-8 backdrop-blur-sm border border-blue-500/30">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold gradient-text mb-2">13+</div>
                    <div className="text-gray-400">Years Experience</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold gradient-text mb-2">500+</div>
                    <div className="text-gray-400">Projects Done</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold gradient-text mb-2">200+</div>
                    <div className="text-gray-400">Happy Clients</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold gradient-text mb-2">24/7</div>
                    <div className="text-gray-400">Support</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="gradient-text">Our Journey</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Key milestones that shaped our growth and success over the years
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-600 to-cyan-600 rounded-full" />

            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative flex items-center mb-16 ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
              >
                {/* Content */}
                <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                  <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-6 border border-gray-700/50">
                    <div className="text-2xl font-bold gradient-text mb-2">{milestone.year}</div>
                    <h3 className="text-xl font-semibold text-white mb-2">{milestone.title}</h3>
                    <p className="text-gray-400">{milestone.description}</p>
                  </div>
                </div>

                {/* Timeline Dot */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full border-4 border-black" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="gradient-text">What Drives Us</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Our core values and principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 glow-blue">
                    <SafeIcon 
                      icon={value.icon} 
                      className={`text-3xl ${value.color}`} 
                    />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{value.title}</h3>
                <p className="text-gray-400 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-blue-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300 mb-8">
              <SafeIcon icon={FiMapPin} className="text-lg" />
              <span>Proudly Serving Kashmir</span>
            </div>

            <h2 className="text-4xl font-bold mb-6">
              <span className="gradient-text">Located in the Heart</span>
              <br />
              <span className="text-white">of Baramulla</span>
            </h2>

            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
              Based in Fatehgarh, Baramulla, we serve the entire Jammu & Kashmir region. 
              Our local presence ensures quick response times and personalized service for all our customers.
            </p>

            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-8 border border-gray-700/50 max-w-2xl mx-auto">
              <h3 className="text-2xl font-semibold text-white mb-4">Service Areas</h3>
              <div className="grid grid-cols-2 gap-4 text-gray-300">
                <div>• Fatehgarh</div>
                <div>• Baramulla</div>
                <div>• Sopore</div>
                <div>• Pattan</div>
                <div>• Uri</div>
                <div>• Tangmarg</div>
                <div>• Gulmarg</div>
                <div>• Srinagar</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;